/**
 * WIP DASHBOARD - Work In Progress Tracker
 * 
 * A private dashboard showing items that need attention:
 * - Items without photos
 * - Items flagged as "Needs Info"
 * - Items missing color/finish tags
 * - Items missing release info
 * - Items not yet swatched
 * 
 * Features:
 * - Progress bars showing completion %
 * - Click to navigate directly to item
 * - Watch numbers shrink as you complete tasks!
 * - Always private (not shown on public profile)
 * - Data Management tools (cleanup, export, import)
 */

import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { collection, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getCurrentUser } from '../firebase/auth';
import { getAuth } from 'firebase/auth';
import {
  Camera, Tag, Calendar, Info, Palette, ChevronRight,
  CheckCircle2, AlertCircle, TrendingDown, Sparkles,
  ArrowLeft, Eye, Filter, Download, Loader2, Check,
  Copy, Clock, Package, Search, AlertTriangle, ShoppingBag, Heart, HardHat,
  ChevronDown, EyeOff, RotateCcw, Settings
} from 'lucide-react';
import BrandCategoryManager from '../components/BrandCategoryManager';
import CollectionCleanup from '../components/CollectionCleanup';
import ColorTaggerModal from '../components/ColorTaggerModal';
import PlateTaggerModal from '../components/PlateTaggerModal';
import { PageHeader } from '../components/common';
import type { CollectionItem } from '../types';

interface WIPCategory {
  id: string;
  title: string;
  description: string;
  emoji: string;
  icon: React.ElementType;
  items: CollectionItem[];
  color: string;
  linkFilter?: string; // URL param to filter in Collection
}

interface HealthInsight {
  id: string;
  title: string;
  description: string;
  emoji: string;
  icon: React.ElementType;
  count: number;
  items: CollectionItem[];
  color: string;
  actionLabel?: string;
  severity: 'info' | 'warning' | 'error';
}

interface DuplicateGroup {
  key: string;
  brand: string;
  name: string;
  items: CollectionItem[];
  isLikelyFalsePositive: boolean; // True if items have different collections/years
}

interface BrandVariant {
  canonical: string;
  variants: string[];
  itemCounts: Record<string, number>;
}

export default function WIPDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [allItems, setAllItems] = useState<CollectionItem[]>([]);
  const [categories, setCategories] = useState<WIPCategory[]>([]);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [totalItems, setTotalItems] = useState(0);
  const [completedItems, setCompletedItems] = useState(0);

  // Collection Health state
  const [healthInsights, setHealthInsights] = useState<HealthInsight[]>([]);
  const [duplicateGroups, setDuplicateGroups] = useState<DuplicateGroup[]>([]);
  const [brandVariants, setBrandVariants] = useState<BrandVariant[]>([]);
  const [expandedHealth, setExpandedHealth] = useState<string | null>(null);
  const [showHealthSection, setShowHealthSection] = useState(true);
  
  // Expanded item counts for "show more" functionality
  const [expandedItemCounts, setExpandedItemCounts] = useState<Record<string, number>>({});
  
  // Show false positive duplicates (items with different collections/years)
  const [showFalsePositiveDupes, setShowFalsePositiveDupes] = useState(false);
  
  // Dismissed items - stored in localStorage
  const [dismissedItems, setDismissedItems] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem('wip-dismissed-items');
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch {
      return new Set();
    }
  });

  // Data Management state
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState('');
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportSuccess, setExportSuccess] = useState(false);
  const [showCleanupModal, setShowCleanupModal] = useState(false);
  const [showColorTagger, setShowColorTagger] = useState(false);
  const [showPlateTagger, setShowPlateTagger] = useState(false);
  const [showBrandManager, setShowBrandManager] = useState(false);
  const [uniqueBrands, setUniqueBrands] = useState<string[]>([]);
  const [userId, setUserId] = useState<string>('');

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user) {
        navigate('/login');
      } else {
        loadItems();
      }
    });
    return () => unsubscribe();
  }, []);

  const loadItems = async () => {
    setLoading(true);
    try {
      const user = getCurrentUser();
      if (!user) return;
      setUserId(user.uid);

      const itemsRef = collection(db, 'users', user.uid, 'items');
      const snapshot = await getDocs(itemsRef);
      const items = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as CollectionItem[];

      // Filter to only active items (not deleted, not destashed)
      const activeItems = items.filter(item => 
        !item.isDeleted && !item.isDestashed && item.itemType === 'polish'
      );
      
      // Keep all items for cross-checks (wishlist vs owned)
      const allActiveItems = items.filter(item => !item.isDeleted && !item.isDestashed);
      
      setAllItems(activeItems);
      setTotalItems(activeItems.length);
      setUniqueBrands(Array.from(new Set(allActiveItems.map(i => i.brand).filter(Boolean))) as string[]);
      
      analyzeItems(activeItems);
      analyzeCollectionHealth(allActiveItems);
    } catch (error) {
      console.error('Error loading items:', error);
    } finally {
      setLoading(false);
    }
  };

  const analyzeItems = (items: CollectionItem[]) => {
    // Items without photos
    const needsPhoto = items.filter(item => {
      const hasPhoto = (item.photos && item.photos.length > 0) ||
        item.photoUrl ||
        (item.swatchPhotos && item.swatchPhotos.length > 0);
      return !hasPhoto;
    });

    // Items flagged as needs info
    const needsInfo = items.filter(item => item.needsInfo === true);

    // Items not swatched
    const notSwatched = items.filter(item => item.swatched !== true);

    // Items missing color/finish tags
    const missingTags = items.filter(item => {
      const hasColorFamily = item.colorFamily && item.colorFamily.length > 0;
      const hasFinish = item.finish && item.finish.length > 0;
      const hasBaseType = item.baseType;
      return !hasColorFamily && !hasFinish && !hasBaseType;
    });

    // Items missing release info
    const missingRelease = items.filter(item => {
      const hasYear = item.releaseYear;
      const hasSeason = item.releaseSeason;
      const hasReleaseType = item.releaseType && item.releaseType !== 'unknown';
      return !hasYear && !hasSeason && !hasReleaseType;
    });

    // Items missing color description
    const missingDescription = items.filter(item => 
      !item.colorDescription || item.colorDescription.trim() === ''
    );

    const wipCategories: WIPCategory[] = [
      {
        id: 'needs-photo',
        title: 'Needs Photos',
        description: 'Items without any swatch or product photos',
        emoji: '📷',
        icon: Camera,
        items: needsPhoto,
        color: 'rose',
        linkFilter: 'no-photo',
      },
      {
        id: 'needs-info',
        title: 'Flagged for Review',
        description: 'Items you\'ve marked as needing more info',
        emoji: '❓',
        icon: Info,
        items: needsInfo,
        color: 'amber',
        linkFilter: 'needs-info',
      },
      {
        id: 'not-swatched',
        title: 'Not Yet Swatched',
        description: 'Polishes waiting to be swatched',
        emoji: '🎨',
        icon: Palette,
        items: notSwatched,
        color: 'purple',
        linkFilter: 'not-swatched',
      },
      {
        id: 'missing-tags',
        title: 'Missing Tags',
        description: 'No color family, finish, or base type',
        emoji: '🏷️',
        icon: Tag,
        items: missingTags,
        color: 'cyan',
        linkFilter: 'no-tags',
      },
      {
        id: 'missing-release',
        title: 'Missing Release Info',
        description: 'No year, season, or release type',
        emoji: '📅',
        icon: Calendar,
        items: missingRelease,
        color: 'emerald',
        linkFilter: 'no-release',
      },
      {
        id: 'missing-description',
        title: 'Missing Description',
        description: 'No color description entered',
        emoji: '📝',
        icon: AlertCircle,
        items: missingDescription,
        color: 'slate',
        linkFilter: 'no-description',
      },
    ];

    setCategories(wipCategories);

    // Calculate "complete" items (has photo AND swatched AND has tags)
    const complete = items.filter(item => {
      const hasPhoto = (item.photos && item.photos.length > 0) || item.photoUrl || (item.swatchPhotos && item.swatchPhotos.length > 0);
      const isSwatched = item.swatched === true;
      const hasTags = (item.colorFamily && item.colorFamily.length > 0) || 
                      (item.finish && item.finish.length > 0) || 
                      item.baseType;
      return hasPhoto && isSwatched && hasTags;
    });
    setCompletedItems(complete.length);
  };

  // Analyze collection health issues
  const analyzeCollectionHealth = (items: CollectionItem[]) => {
    const insights: HealthInsight[] = [];
    
    // 1. WISHLIST VS COLLECTION - Items on wishlist that you already own
    const wishlistItems = items.filter(item => item.onWishlist === true);
    const ownedItems = items.filter(item => (item.quantity || 0) > 0 && !item.onWishlist);
    
    // Find wishlist items that match owned items by name+brand
    // Also check collection name and release year - if different, they might be different polishes
    const wishlistAlreadyOwned: CollectionItem[] = [];
    wishlistItems.forEach(wishItem => {
      const normalizedWishName = (wishItem.name || '').toLowerCase().trim();
      const normalizedWishBrand = (wishItem.brand || '').toLowerCase().trim();
      const wishCollection = (wishItem.collectionName || '').toLowerCase().trim();
      const wishYear = wishItem.releaseYear;
      
      const match = ownedItems.find(owned => {
        const normalizedOwnedName = (owned.name || '').toLowerCase().trim();
        const normalizedOwnedBrand = (owned.brand || '').toLowerCase().trim();
        const ownedCollection = (owned.collectionName || '').toLowerCase().trim();
        const ownedYear = owned.releaseYear;
        
        // Must match name and brand
        if (normalizedWishName !== normalizedOwnedName || normalizedWishBrand !== normalizedOwnedBrand) {
          return false;
        }
        
        // If both have collection names and they differ, not a match
        if (wishCollection && ownedCollection && wishCollection !== ownedCollection) {
          return false;
        }
        
        // If both have release years and they differ, not a match
        if (wishYear && ownedYear && wishYear !== ownedYear) {
          return false;
        }
        
        return true;
      });
      
      if (match) {
        wishlistAlreadyOwned.push(wishItem);
      }
    });
    
    if (wishlistAlreadyOwned.length > 0) {
      insights.push({
        id: 'wishlist-owned',
        title: 'Already Owned!',
        description: 'These wishlist items match polishes you already own',
        emoji: '🎉',
        icon: ShoppingBag,
        count: wishlistAlreadyOwned.length,
        items: wishlistAlreadyOwned,
        color: 'green',
        actionLabel: 'Remove from wishlist',
        severity: 'info'
      });
    }

    // 2. DUPLICATE DETECTION - Same name + brand (with collection/year check)
    const itemMap = new Map<string, CollectionItem[]>();
    items.forEach(item => {
      const key = `${(item.brand || '').toLowerCase().trim()}|||${(item.name || '').toLowerCase().trim()}`;
      if (!itemMap.has(key)) {
        itemMap.set(key, []);
      }
      itemMap.get(key)!.push(item);
    });
    
    const duplicates: DuplicateGroup[] = [];
    itemMap.forEach((itemList, key) => {
      if (itemList.length > 1) {
        const [brand, name] = key.split('|||');
        
        // Check if items have different collection names or release years
        // If so, they're likely different polishes with the same name
        const hasVariedCollections = itemList.some((item, idx) => {
          for (let i = idx + 1; i < itemList.length; i++) {
            const other = itemList[i];
            // Check collection name
            const itemCollection = (item.collectionName || '').toLowerCase().trim();
            const otherCollection = (other.collectionName || '').toLowerCase().trim();
            if (itemCollection && otherCollection && itemCollection !== otherCollection) {
              return true;
            }
            // Check release year
            if (item.releaseYear && other.releaseYear && item.releaseYear !== other.releaseYear) {
              return true;
            }
          }
          return false;
        });
        
        duplicates.push({
          key,
          brand: itemList[0].brand || brand,
          name: itemList[0].name || name,
          items: itemList,
          isLikelyFalsePositive: hasVariedCollections
        });
      }
    });
    
    setDuplicateGroups(duplicates);
    
    // Only count true duplicates (not likely false positives) in the insight
    const trueDuplicates = duplicates.filter(g => !g.isLikelyFalsePositive);
    
    if (trueDuplicates.length > 0) {
      const totalDuplicateItems = trueDuplicates.reduce((sum, g) => sum + g.items.length, 0);
      insights.push({
        id: 'duplicates',
        title: 'Potential Duplicates',
        description: `${trueDuplicates.length} groups of items with the same name and brand`,
        emoji: '👯',
        icon: Copy,
        count: totalDuplicateItems,
        items: trueDuplicates.flatMap(g => g.items),
        color: 'amber',
        actionLabel: 'Review & merge',
        severity: 'warning'
      });
    }

    // 3. BRAND NAME INCONSISTENCIES
    const brandCounts = new Map<string, number>();
    items.forEach(item => {
      const brand = (item.brand || '').trim();
      if (brand) {
        brandCounts.set(brand, (brandCounts.get(brand) || 0) + 1);
      }
    });
    
    // Group similar brand names (case-insensitive, ignoring minor differences)
    const brandGroups = new Map<string, string[]>();
    const processedBrands = new Set<string>();
    
    Array.from(brandCounts.keys()).forEach(brand => {
      if (processedBrands.has(brand)) return;
      
      const normalizedBrand = brand.toLowerCase().replace(/[^a-z0-9]/g, '');
      const similarBrands = Array.from(brandCounts.keys()).filter(b => {
        if (processedBrands.has(b)) return false;
        const normalizedB = b.toLowerCase().replace(/[^a-z0-9]/g, '');
        // Same when normalized but different original
        return normalizedBrand === normalizedB && b !== brand;
      });
      
      if (similarBrands.length > 0) {
        const allVariants = [brand, ...similarBrands];
        // Use the most common one as canonical
        const canonical = allVariants.reduce((a, b) => 
          (brandCounts.get(a) || 0) >= (brandCounts.get(b) || 0) ? a : b
        );
        brandGroups.set(canonical, allVariants.filter(v => v !== canonical));
        allVariants.forEach(v => processedBrands.add(v));
      }
    });
    
    const variants: BrandVariant[] = [];
    brandGroups.forEach((variantList, canonical) => {
      const itemCounts: Record<string, number> = {};
      itemCounts[canonical] = brandCounts.get(canonical) || 0;
      variantList.forEach(v => {
        itemCounts[v] = brandCounts.get(v) || 0;
      });
      variants.push({ canonical, variants: variantList, itemCounts });
    });
    
    setBrandVariants(variants);
    
    if (variants.length > 0) {
      const affectedItems = variants.reduce((sum, v) => {
        return sum + v.variants.reduce((s, variant) => s + (v.itemCounts[variant] || 0), 0);
      }, 0);
      
      insights.push({
        id: 'brand-variants',
        title: 'Brand Name Inconsistencies',
        description: `${variants.length} brands have spelling variations`,
        emoji: '📝',
        icon: AlertTriangle,
        count: affectedItems,
        items: [], // Complex - handled specially
        color: 'orange',
        actionLabel: 'Standardize names',
        severity: 'warning'
      });
    }

    // 4. STALE WISHLIST - Items on wishlist for over 1 year
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    
    const staleWishlist = wishlistItems.filter(item => {
      if (!item.createdAt) return false;
      const createdDate = item.createdAt instanceof Timestamp 
        ? item.createdAt.toDate() 
        : new Date(item.createdAt);
      return createdDate < oneYearAgo;
    });
    
    if (staleWishlist.length > 0) {
      insights.push({
        id: 'stale-wishlist',
        title: 'Long-time Wishlist Items',
        description: 'On your wishlist for over a year - still interested?',
        emoji: '⏰',
        icon: Clock,
        count: staleWishlist.length,
        items: staleWishlist,
        color: 'blue',
        actionLabel: 'Review wishlist',
        severity: 'info'
      });
    }

    // 5. UNTRIED ITEMS - Owned but never used
    const untriedOwned = items.filter(item => 
      (item.quantity || 0) > 0 && 
      !item.onWishlist && 
      (item.timesUsed || 0) === 0 &&
      item.swatched !== true
    );
    
    if (untriedOwned.length > 0) {
      insights.push({
        id: 'untried',
        title: 'Untried Polishes',
        description: 'You own these but haven\'t used or swatched them yet',
        emoji: '✨',
        icon: Package,
        count: untriedOwned.length,
        items: untriedOwned,
        color: 'purple',
        actionLabel: 'Try something new!',
        severity: 'info'
      });
    }

    // 6. PLACEHOLDER NAMES - Items with generic/placeholder names
    const placeholderPatterns = [
      /^test$/i, /^unknown$/i, /^untitled$/i, /^\?+$/, /^xxx$/i,
      /^polish$/i, /^new polish$/i, /^sample$/i, /^temp$/i,
      /^tbd$/i, /^n\/a$/i, /^none$/i, /^\.+$/
    ];
    
    const placeholderItems = items.filter(item => {
      const name = (item.name || '').trim();
      if (!name || name.length < 2) return true;
      return placeholderPatterns.some(pattern => pattern.test(name));
    });
    
    if (placeholderItems.length > 0) {
      insights.push({
        id: 'placeholder',
        title: 'Placeholder Names',
        description: 'Items with generic or incomplete names',
        emoji: '🏷️',
        icon: Search,
        count: placeholderItems.length,
        items: placeholderItems,
        color: 'red',
        actionLabel: 'Add proper names',
        severity: 'error'
      });
    }

    // 7. MISSING BRAND - Items without a brand name
    const missingBrand = items.filter(item => 
      !item.brand || item.brand.trim() === ''
    );
    
    if (missingBrand.length > 0) {
      insights.push({
        id: 'missing-brand',
        title: 'Missing Brand',
        description: 'Items without a brand name',
        emoji: '🏪',
        icon: AlertCircle,
        count: missingBrand.length,
        items: missingBrand,
        color: 'red',
        actionLabel: 'Add brand names',
        severity: 'error'
      });
    }

    // Sort by severity (errors first, then warnings, then info)
    const severityOrder = { error: 0, warning: 1, info: 2 };
    insights.sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity]);
    
    setHealthInsights(insights);
  };

  // Dismiss an item from health checks
  const dismissItem = (itemId: string) => {
    setDismissedItems(prev => {
      const newSet = new Set(prev);
      newSet.add(itemId);
      localStorage.setItem('wip-dismissed-items', JSON.stringify([...newSet]));
      return newSet;
    });
  };

  // Restore a dismissed item
  const restoreItem = (itemId: string) => {
    setDismissedItems(prev => {
      const newSet = new Set(prev);
      newSet.delete(itemId);
      localStorage.setItem('wip-dismissed-items', JSON.stringify([...newSet]));
      return newSet;
    });
  };

  // Clear all dismissed items
  const clearAllDismissed = () => {
    setDismissedItems(new Set());
    localStorage.removeItem('wip-dismissed-items');
  };

  // Get the number of items to show for a section
  const getItemsToShow = (sectionId: string, defaultCount: number = 5) => {
    return expandedItemCounts[sectionId] || defaultCount;
  };

  // Toggle showing more/less items for a section
  const toggleExpandItems = (sectionId: string, totalItems: number) => {
    setExpandedItemCounts(prev => {
      const currentCount = prev[sectionId] || 5;
      // If already showing all, collapse to 5
      if (currentCount >= totalItems) {
        return { ...prev, [sectionId]: 5 };
      }
      // Otherwise show all
      return { ...prev, [sectionId]: totalItems };
    });
  };

  // Filter out dismissed items from a list
  const filterDismissed = (items: CollectionItem[]) => {
    return items.filter(item => !dismissedItems.has(item.id));
  };

  const getProgressColor = (percentage: number): string => {
    if (percentage >= 80) return 'bg-green-500';
    if (percentage >= 60) return 'bg-emerald-500';
    if (percentage >= 40) return 'bg-yellow-500';
    if (percentage >= 20) return 'bg-orange-500';
    return 'bg-rose-500';
  };

  const getCategoryColor = (color: string): { bg: string; text: string; border: string } => {
    const colors: Record<string, { bg: string; text: string; border: string }> = {
      rose: { bg: 'bg-rose-500/40', text: 'text-rose-400', border: 'border-rose-500/60' },
      amber: { bg: 'bg-amber-500/40', text: 'text-amber-400', border: 'border-amber-500/60' },
      purple: { bg: 'bg-purple-500/40', text: 'text-purple-400', border: 'border-purple-500/60' },
      cyan: { bg: 'bg-cyan-500/40', text: 'text-cyan-400', border: 'border-cyan-500/60' },
      emerald: { bg: 'bg-emerald-500/40', text: 'text-emerald-400', border: 'border-emerald-500/60' },
      slate: { bg: 'bg-slate-500/40', text: 'text-slate-400', border: 'border-slate-500/60' },
    };
    return colors[color] || colors.slate;
  };

  // Export collection to CSV
  const handleExportCollection = async () => {
    const currentUser = getCurrentUser();
    if (!currentUser) return;

    setIsExporting(true);
    setExportProgress('Loading your collection...');
    setExportError(null);
    setExportSuccess(false);

    try {
      // Fetch all items
      const itemsRef = collection(db, 'users', currentUser.uid, 'items');
      const snapshot = await getDocs(itemsRef);
      
      if (snapshot.empty) {
        setExportError('No items found in your collection.');
        setIsExporting(false);
        return;
      }

      setExportProgress(`Processing ${snapshot.docs.length} items...`);

      // Define all columns (order matters for CSV)
      const columns = [
        // Core
        'id', 'itemType', 'brand', 'name', 'quantity', 'category', 'colorDescription', 'polishType',
        // Release info
        'releaseType', 'releaseYear', 'releaseSeason',
        'collectionName',
        'eventSource', 'eventTheme',
        'boxName', 'boxTheme',
        'groupName', 'groupTheme',
        'exclusiveTo', 'exclusiveTheme',
        'charityName', 'charityCause',
        'conventionName',
        'collabWith', 'collabTheme',
        'setName', 'setType',
        // Re-release
        'isRerelease', 'originalReleaseYear', 'originalReleaseSeason', 'releaseNotes',
        // Structured tags (arrays become comma-separated)
        'baseType', 'finish', 'holoType', 'colorShift', 'particles', 
        'specialFeatures', 'colorFamily', 'utility', 'colorCharacter',
        // Status
        'swatched', 'hasSwatched', 'timesUsed', 'isFavorite', 'isRareLE', 'lastUsedDate',
        // Storage
        'storage', 'barcode',
        // Notes
        'notes', 'publicNotes',
        // Wishlist/Destash
        'onWishlist', 'isoPriority', 'isForDestash', 'destashPrice', 'destashCondition',
        // Marketplace
        'isForSale', 'price', 'condition',
        // Legacy tags (for reference)
        'tags', 'finishTypes',
        // Photos (first URL only for reference)
        'photoUrl',
        // Metadata
        'dateAdded'
      ];

      // Build rows
      const rows: string[][] = [];
      
      for (const doc of snapshot.docs) {
        const item = doc.data();
        const row: string[] = [];
        
        for (const col of columns) {
          let value = item[col];
          
          // Handle different types
          if (value === undefined || value === null) {
            row.push('');
          } else if (Array.isArray(value)) {
            // Arrays become comma-separated
            row.push(value.join(', '));
          } else if (typeof value === 'boolean') {
            row.push(value ? 'Yes' : 'No');
          } else if (value instanceof Timestamp || (value && value.toDate)) {
            // Firestore timestamps
            const date = value.toDate ? value.toDate() : value;
            row.push(date.toISOString().split('T')[0]);
          } else if (value instanceof Date) {
            row.push(value.toISOString().split('T')[0]);
          } else if (col === 'photoUrl' && !item.photoUrl && item.photos?.length > 0) {
            // Get first photo URL if photoUrl not set but photos array exists
            row.push(item.photos[0]?.url || '');
          } else {
            row.push(String(value));
          }
        }
        
        rows.push(row);
      }

      setExportProgress('Building CSV file...');

      // Escape CSV values (handle commas, quotes, newlines)
      const escapeCSV = (val: string): string => {
        if (val.includes(',') || val.includes('"') || val.includes('\n') || val.includes('\r')) {
          return `"${val.replace(/"/g, '""')}"`;
        }
        return val;
      };

      // Build CSV content
      const csvContent = [
        columns.map(escapeCSV).join(','),
        ...rows.map(row => row.map(escapeCSV).join(','))
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `polish-collection-export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setExportSuccess(true);
      setExportProgress(`Exported ${snapshot.docs.length} items!`);
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setExportSuccess(false);
        setExportProgress('');
      }, 5000);

    } catch (error) {
      console.error('Export error:', error);
      setExportError('Failed to export collection. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="glass-card p-8 text-center">
          <div className="inline-block w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-glass-secondary font-body">Analyzing your collection...</p>
        </div>
      </div>
    );
  }

  const overallProgress = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;
  const totalIssues = categories.reduce((sum, cat) => sum + cat.items.length, 0);

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Back Link */}
      <Link
        to="/collection"
        className="inline-flex items-center gap-2 text-on-bg hover:text-glass mb-4 transition-default font-body"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Collection
      </Link>

      {/* Header */}
      <PageHeader
        icon={HardHat}
        title="Work In Progress"
        subtitle="Track and complete items that need attention. This page is always private."
        className="mb-8"
      />

      {/* Overall Progress Card */}
      <div className="glass-card p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-glass font-heading">Collection Completeness</h2>
            <p className="text-sm text-glass-secondary font-body">
              Items with photo + swatched + tags
            </p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-glass">{overallProgress}%</div>
            <div className="text-sm text-glass-secondary font-body">
              {completedItems} of {totalItems} complete
            </div>
          </div>
        </div>
        <div className="w-full h-4 bg-slate-700 rounded-full overflow-hidden">
          <div 
            className={`h-full ${getProgressColor(overallProgress)} transition-all duration-500`}
            style={{ width: `${overallProgress}%` }}
          />
        </div>
        
        {overallProgress === 100 && (
          <div className="mt-4 p-3 bg-green-500/20 border border-green-500/30 rounded-lg flex items-center gap-3">
            <CheckCircle2 className="w-6 h-6 text-green-400" />
            <div>
              <div className="font-semibold text-green-400">🏆 Completely Coated!</div>
              <div className="text-sm text-green-300/80">Your entire collection is fully documented!</div>
            </div>
          </div>
        )}
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="glass-card p-4 text-center">
          <div className="text-2xl font-bold text-glass">{totalItems}</div>
          <div className="text-xs text-glass-secondary font-body">Total Polishes</div>
        </div>
        <div className="glass-card p-4 text-center">
          <div className="text-2xl font-bold text-green-400">{completedItems}</div>
          <div className="text-xs text-glass-secondary font-body">Complete</div>
        </div>
        <div className="glass-card p-4 text-center">
          <div className="text-2xl font-bold text-amber-400">{totalIssues}</div>
          <div className="text-xs text-glass-secondary font-body">Tasks Remaining</div>
        </div>
        <div className="glass-card p-4 text-center">
          <div className="text-2xl font-bold text-purple-400">
            {categories.find(c => c.id === 'not-swatched')?.items.length || 0}
          </div>
          <div className="text-xs text-glass-secondary font-body">To Swatch</div>
        </div>
      </div>

      {/* Swatch Badges - Hybrid System */}
      {(() => {
        const swatchedCount = totalItems - (categories.find(c => c.id === 'not-swatched')?.items.length || 0);
        const swatchedPercent = totalItems > 0 ? Math.round((swatchedCount / totalItems) * 100) : 0;
        
        // Percentage-based badges (Completion)
        const percentBadges = [
          { name: 'Swatch Starter', emoji: '🎨', threshold: 25, earned: swatchedPercent >= 25 },
          { name: 'Swatch Star', emoji: '⭐', threshold: 50, earned: swatchedPercent >= 50 },
          { name: 'Swatch Superstar', emoji: '🌟', threshold: 75, earned: swatchedPercent >= 75 },
          { name: 'Swatch Sovereign', emoji: '👑', threshold: 90, earned: swatchedPercent >= 90 },
          { name: 'Completely Coated', emoji: '🏆', threshold: 100, earned: swatchedPercent >= 100 },
        ];
        
        // Volume-based badges (Effort)
        const volumeBadges = [
          { name: 'Century Club', emoji: '💪', threshold: 100, earned: swatchedCount >= 100 },
          { name: 'Five Hundred Strong', emoji: '🔥', threshold: 500, earned: swatchedCount >= 500 },
          { name: 'Thousand Touch', emoji: '✨', threshold: 1000, earned: swatchedCount >= 1000 },
        ];
        
        const earnedPercentBadges = percentBadges.filter(b => b.earned);
        const earnedVolumeBadges = volumeBadges.filter(b => b.earned);
        const nextPercentBadge = percentBadges.find(b => !b.earned);
        const nextVolumeBadge = volumeBadges.find(b => !b.earned);

        return (
          <div className="glass-card p-5 mb-8">
            <h3 className="text-lg font-bold text-glass mb-4 font-heading">🎖️ Swatch Badges</h3>
            
            <div className="grid md:grid-cols-2 gap-6">
              {/* Completion Badges (Percentage) */}
              <div>
                <div className="text-sm font-semibold text-glass-secondary mb-3 font-body flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                  Completion ({swatchedPercent}% swatched)
                </div>
                <div className="space-y-2">
                  {percentBadges.map(badge => (
                    <div 
                      key={badge.name}
                      className={`flex items-center justify-between p-2 rounded-lg ${
                        badge.earned 
                          ? 'bg-purple-500/20 border border-purple-500/30' 
                          : 'bg-slate-800/40 border border-slate-700 opacity-50'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className={badge.earned ? '' : 'grayscale'}>{badge.emoji}</span>
                        <span className={`text-sm font-body ${badge.earned ? 'text-glass' : 'text-glass-muted'}`}>
                          {badge.name}
                        </span>
                      </div>
                      <span className={`text-xs font-body ${badge.earned ? 'text-purple-400' : 'text-glass-muted'}`}>
                        {badge.threshold}%
                      </span>
                    </div>
                  ))}
                </div>
                {nextPercentBadge && (
                  <div className="mt-3 text-xs text-glass-muted font-body">
                    Next: {nextPercentBadge.emoji} {nextPercentBadge.name} at {nextPercentBadge.threshold}% 
                    ({nextPercentBadge.threshold - swatchedPercent}% to go)
                  </div>
                )}
              </div>
              
              {/* Volume Badges (Count) */}
              <div>
                <div className="text-sm font-semibold text-glass-secondary mb-3 font-body flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                  Volume ({swatchedCount} swatched)
                </div>
                <div className="space-y-2">
                  {volumeBadges.map(badge => (
                    <div 
                      key={badge.name}
                      className={`flex items-center justify-between p-2 rounded-lg ${
                        badge.earned 
                          ? 'bg-amber-500/20 border border-amber-500/30' 
                          : 'bg-slate-800/40 border border-slate-700 opacity-50'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className={badge.earned ? '' : 'grayscale'}>{badge.emoji}</span>
                        <span className={`text-sm font-body ${badge.earned ? 'text-glass' : 'text-glass-muted'}`}>
                          {badge.name}
                        </span>
                      </div>
                      <span className={`text-xs font-body ${badge.earned ? 'text-amber-400' : 'text-glass-muted'}`}>
                        {badge.threshold.toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
                {nextVolumeBadge && (
                  <div className="mt-3 text-xs text-glass-muted font-body">
                    Next: {nextVolumeBadge.emoji} {nextVolumeBadge.name} at {nextVolumeBadge.threshold.toLocaleString()} 
                    ({(nextVolumeBadge.threshold - swatchedCount).toLocaleString()} to go)
                  </div>
                )}
              </div>
            </div>
            
            {/* Summary of earned badges */}
            {(earnedPercentBadges.length > 0 || earnedVolumeBadges.length > 0) && (
              <div className="mt-4 pt-4 border-t border-slate-700">
                <div className="text-sm text-glass-secondary font-body">
                  Earned: {[...earnedPercentBadges, ...earnedVolumeBadges].map(b => b.emoji).join(' ')}
                  <span className="text-glass-muted ml-2">
                    ({earnedPercentBadges.length + earnedVolumeBadges.length} of {percentBadges.length + volumeBadges.length} badges)
                  </span>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Rare/LE Badges */}
      {(() => {
        // Count rare/LE items (need to calculate from allItems since categories doesn't have this)
        const rareCount = allItems.filter(item => item.isRareLE).length;
        
        // Rare/LE badges
        const rareBadges = [
          { name: 'Rare Recruit', emoji: '💎', threshold: 10, earned: rareCount >= 10 },
          { name: 'Rarity Ranger', emoji: '🔍', threshold: 25, earned: rareCount >= 25 },
          { name: 'Rarity Royalty', emoji: '👑', threshold: 50, earned: rareCount >= 50 },
          { name: 'Legendary Luminary', emoji: '✨', threshold: 100, earned: rareCount >= 100 },
        ];
        
        const earnedRareBadges = rareBadges.filter(b => b.earned);
        const nextRareBadge = rareBadges.find(b => !b.earned);

        return (
          <div className="glass-card p-5 mb-8">
            <h3 className="text-lg font-bold text-glass mb-4 font-heading">💎 Rare/LE Badges</h3>
            
            <div className="text-sm font-semibold text-glass-secondary mb-3 font-body flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-violet-500"></span>
              Collection ({rareCount} rare/limited edition items)
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {rareBadges.map(badge => (
                <div 
                  key={badge.name}
                  className={`flex flex-col items-center p-3 rounded-lg ${
                    badge.earned 
                      ? 'bg-violet-500/20 border border-violet-500/30' 
                      : 'bg-slate-800/40 border border-slate-700 opacity-50'
                  }`}
                >
                  <span className={`text-2xl mb-1 ${badge.earned ? '' : 'grayscale'}`}>{badge.emoji}</span>
                  <span className={`text-xs font-body text-center ${badge.earned ? 'text-glass' : 'text-glass-muted'}`}>
                    {badge.name}
                  </span>
                  <span className={`text-xs font-body ${badge.earned ? 'text-violet-400' : 'text-glass-muted'}`}>
                    {badge.threshold}+
                  </span>
                </div>
              ))}
            </div>
            
            {nextRareBadge && rareCount > 0 && (
              <div className="mt-3 text-xs text-glass-muted font-body">
                Next: {nextRareBadge.emoji} {nextRareBadge.name} at {nextRareBadge.threshold} 
                ({nextRareBadge.threshold - rareCount} to go)
              </div>
            )}
            
            {earnedRareBadges.length > 0 && (
              <div className="mt-3 pt-3 border-t border-slate-700">
                <div className="text-sm text-glass-secondary font-body">
                  Earned: {earnedRareBadges.map(b => b.emoji).join(' ')}
                  <span className="text-glass-muted ml-2">
                    ({earnedRareBadges.length} of {rareBadges.length} badges)
                  </span>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Collection Health Section */}
      {healthInsights.length > 0 && (
        <div className="glass-card p-5 mb-8">
          <div className="flex items-center justify-between mb-4">
            <button 
              onClick={() => setShowHealthSection(!showHealthSection)}
              className="flex items-center gap-2 flex-1"
            >
              <h3 className="text-lg font-bold text-glass font-heading flex items-center gap-2">
                <Heart className="w-5 h-5 text-pink-400" />
                Collection Health
                <span className="text-sm font-normal text-glass-secondary">
                  ({healthInsights.reduce((sum, h) => sum + filterDismissed(h.items).length, 0)} items need attention)
                </span>
              </h3>
              <ChevronRight className={`w-5 h-5 text-glass-secondary transition-transform ${showHealthSection ? 'rotate-90' : ''}`} />
            </button>
            
            {/* Show hidden items count and restore button */}
            {dismissedItems.size > 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  clearAllDismissed();
                }}
                className="text-xs text-glass-muted hover:text-purple-400 flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-slate-800/40 transition-default font-body"
                title="Show all hidden items"
              >
                <RotateCcw className="w-3 h-3" />
                {dismissedItems.size} hidden
              </button>
            )}
          </div>
          
          {showHealthSection && (
            <div className="space-y-3">
              {healthInsights.map(insight => {
                const isExpanded = expandedHealth === insight.id;
                const activeItemCount = filterDismissed(insight.items).length;
                const severityColors = {
                  error: { bg: 'bg-red-500/40', border: 'border-red-500/60', text: 'text-red-400', icon: 'text-red-400' },
                  warning: { bg: 'bg-amber-500/40', border: 'border-amber-500/60', text: 'text-amber-400', icon: 'text-amber-400' },
                  info: { bg: 'bg-blue-500/40', border: 'border-blue-500/60', text: 'text-blue-400', icon: 'text-blue-400' }
                };
                const colors = severityColors[insight.severity];
                const Icon = insight.icon;
                
                // Skip insights where all items are dismissed (except special types)
                if (activeItemCount === 0 && insight.id !== 'brand-variants' && insight.id !== 'duplicates') {
                  return null;
                }
                
                return (
                  <div key={insight.id} className={`rounded-lg border ${colors.border} overflow-hidden`}>
                    <button
                      onClick={() => setExpandedHealth(isExpanded ? null : insight.id)}
                      className={`w-full p-3 flex items-center justify-between hover:bg-slate-800/20 transition-default ${colors.bg}`}
                    >
                      <div className="flex items-center gap-3">
                        <Icon className={`w-5 h-5 ${colors.icon}`} />
                        <div className="text-left">
                          <div className="font-medium text-glass font-body flex items-center gap-2">
                            {insight.emoji} {insight.title}
                            <span className={`px-2 py-0.5 text-xs rounded-full ${colors.bg} ${colors.text}`}>
                              {insight.id === 'brand-variants' || insight.id === 'duplicates' ? insight.count : activeItemCount}
                            </span>
                          </div>
                          <div className="text-xs text-glass-secondary font-body">{insight.description}</div>
                        </div>
                      </div>
                      <ChevronRight className={`w-4 h-4 text-glass-secondary transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                    </button>
                    
                    {isExpanded && (
                      <div className="border-t border-slate-700">
                        {/* Special handling for brand variants */}
                        {insight.id === 'brand-variants' && brandVariants.length > 0 ? (
                          <div className="p-3 space-y-2 max-h-64 overflow-y-auto">
                            {brandVariants.map((variant, idx) => (
                              <div key={idx} className="p-2 bg-slate-800/40 rounded-lg">
                                <div className="font-medium text-glass text-sm font-body">
                                  ✓ {variant.canonical} ({variant.itemCounts[variant.canonical]} items)
                                </div>
                                <div className="text-xs text-glass-secondary font-body mt-1">
                                  Also spelled as: {variant.variants.map(v => (
                                    <span key={v} className="inline-flex items-center gap-1">
                                      <span className="text-amber-400">{v}</span>
                                      <span className="text-glass-muted">({variant.itemCounts[v]})</span>
                                      {variant.variants.indexOf(v) < variant.variants.length - 1 && ', '}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            ))}
                            <div className="text-xs text-glass-muted pt-2 font-body">
                              💡 Use the Clean Up tool in Data Management to fix these automatically
                            </div>
                          </div>
                        ) : insight.id === 'duplicates' && duplicateGroups.length > 0 ? (
                          <div className="p-3 space-y-2">
                            {/* Toggle for false positive duplicates */}
                            {duplicateGroups.some(g => g.isLikelyFalsePositive) && (
                              <label className="flex items-center gap-2 p-2 bg-slate-800/60 rounded-lg cursor-pointer mb-2">
                                <input
                                  type="checkbox"
                                  checked={showFalsePositiveDupes}
                                  onChange={(e) => setShowFalsePositiveDupes(e.target.checked)}
                                  className="rounded border-slate-500 bg-slate-700 text-purple-500 focus:ring-purple-500"
                                />
                                <span className="text-xs text-glass-secondary font-body">
                                  Show {duplicateGroups.filter(g => g.isLikelyFalsePositive).length} items with different collections/years (likely not duplicates)
                                </span>
                              </label>
                            )}
                            
                            <div className="max-h-80 overflow-y-auto space-y-2">
                              {duplicateGroups
                                .filter(g => showFalsePositiveDupes || !g.isLikelyFalsePositive)
                                .slice(0, getItemsToShow('duplicates', 10))
                                .map((group, idx) => (
                                  <div key={idx} className={`p-2 rounded-lg ${group.isLikelyFalsePositive ? 'bg-slate-800/20 border border-dashed border-slate-600' : 'bg-slate-800/40'}`}>
                                    <div className="flex items-start justify-between gap-2">
                                      <div className="flex-1">
                                        <div className="font-medium text-glass text-sm font-body flex items-center gap-2">
                                          {group.brand} - {group.name}
                                          {group.isLikelyFalsePositive && (
                                            <span className="px-1.5 py-0.5 text-xs bg-blue-500/20 text-blue-300 rounded">
                                              Different collections
                                            </span>
                                          )}
                                        </div>
                                        <div className="text-xs text-glass-secondary font-body mt-1">
                                          {group.items.map((item, i) => (
                                            <span key={item.id}>
                                              {i > 0 && ' • '}
                                              <Link 
                                                to={`/collection?item=${item.id}`}
                                                className="hover:text-purple-400 transition-colors"
                                              >
                                                {item.collectionName || item.releaseYear || 'No collection info'}
                                                {item.releaseYear && item.collectionName ? ` (${item.releaseYear})` : ''}
                                              </Link>
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                      <span className="text-xs text-glass-muted font-body whitespace-nowrap">
                                        {group.items.length} entries
                                      </span>
                                    </div>
                                  </div>
                                ))}
                            </div>
                            
                            {/* Show more/less button */}
                            {duplicateGroups.filter(g => showFalsePositiveDupes || !g.isLikelyFalsePositive).length > 10 && (
                              <button
                                onClick={() => toggleExpandItems('duplicates', duplicateGroups.filter(g => showFalsePositiveDupes || !g.isLikelyFalsePositive).length)}
                                className="w-full p-2 text-sm text-purple-400 hover:text-purple-300 hover:bg-slate-800/40 rounded-lg transition-default font-body flex items-center justify-center gap-1"
                              >
                                {getItemsToShow('duplicates', 10) >= duplicateGroups.filter(g => showFalsePositiveDupes || !g.isLikelyFalsePositive).length ? (
                                  <>
                                    <ChevronDown className="w-4 h-4 rotate-180" />
                                    Show less
                                  </>
                                ) : (
                                  <>
                                    <ChevronDown className="w-4 h-4" />
                                    Show all {duplicateGroups.filter(g => showFalsePositiveDupes || !g.isLikelyFalsePositive).length} groups
                                  </>
                                )}
                              </button>
                            )}
                            
                            <div className="text-xs text-glass-muted pt-2 font-body">
                              💡 Review these items and merge or delete duplicates
                            </div>
                          </div>
                        ) : (
                          <>
                            {/* Filter out dismissed items */}
                            {(() => {
                              const activeItems = filterDismissed(insight.items);
                              const itemsToShow = getItemsToShow(insight.id, 5);
                              const displayedItems = activeItems.slice(0, itemsToShow);
                              const dismissedCount = insight.items.length - activeItems.length;
                              
                              return (
                                <>
                                  <div className="p-3 bg-slate-800/40 flex flex-wrap justify-between items-center gap-2">
                                    <span className="text-sm text-glass-secondary font-body">
                                      Showing {Math.min(itemsToShow, activeItems.length)} of {activeItems.length} items
                                      {dismissedCount > 0 && (
                                        <span className="text-glass-muted ml-1">
                                          ({dismissedCount} hidden)
                                        </span>
                                      )}
                                    </span>
                                    {dismissedCount > 0 && (
                                      <button
                                        onClick={() => {
                                          // Restore all dismissed items for this insight
                                          insight.items.forEach(item => {
                                            if (dismissedItems.has(item.id)) {
                                              restoreItem(item.id);
                                            }
                                          });
                                        }}
                                        className="text-xs text-purple-400 hover:text-purple-300 flex items-center gap-1 font-body"
                                      >
                                        <RotateCcw className="w-3 h-3" />
                                        Show hidden
                                      </button>
                                    )}
                                  </div>
                                  
                                  <div className="divide-y divide-slate-700 max-h-96 overflow-y-auto">
                                    {displayedItems.map(item => (
                                      <div
                                        key={item.id}
                                        className="flex items-center gap-3 p-3 hover:bg-slate-800/40 transition-default group"
                                      >
                                        <Link
                                          to={`/collection?item=${item.id}`}
                                          className="flex items-center gap-3 flex-1 min-w-0"
                                        >
                                          <div className="w-10 h-10 rounded-lg overflow-hidden bg-slate-700 flex-shrink-0">
                                            {(item.photos?.[0] || item.photoUrl || item.swatchPhotos?.[0]) ? (
                                              <img
                                                src={typeof item.photos?.[0] === 'string' ? item.photos[0] : item.photos?.[0]?.url || item.photoUrl || item.swatchPhotos?.[0]}
                                                alt={item.name}
                                                className="w-full h-full object-cover"
                                              />
                                            ) : (
                                              <div className="w-full h-full flex items-center justify-center text-slate-500">
                                                <Camera className="w-4 h-4" />
                                              </div>
                                            )}
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <div className="font-medium text-glass text-sm truncate font-body">{item.name || '(no name)'}</div>
                                            <div className="text-xs text-glass-secondary truncate font-body">
                                              {item.brand || '(no brand)'}
                                              {item.collectionName && <span className="text-glass-muted"> • {item.collectionName}</span>}
                                            </div>
                                          </div>
                                          <Eye className="w-4 h-4 text-glass-muted" />
                                        </Link>
                                        
                                        {/* Dismiss button */}
                                        <button
                                          onClick={(e) => {
                                            e.preventDefault();
                                            dismissItem(item.id);
                                          }}
                                          className="p-1.5 rounded-lg text-glass-muted hover:text-red-400 hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition-all"
                                          title="Don't show this item again"
                                        >
                                          <EyeOff className="w-4 h-4" />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                  
                                  {/* Show more/less button */}
                                  {activeItems.length > 5 && (
                                    <button
                                      onClick={() => toggleExpandItems(insight.id, activeItems.length)}
                                      className="w-full p-3 bg-slate-800/40 text-center hover:bg-slate-800/60 transition-default"
                                    >
                                      <span className="text-sm text-purple-400 hover:text-purple-300 font-body flex items-center justify-center gap-1">
                                        {itemsToShow >= activeItems.length ? (
                                          <>
                                            <ChevronDown className="w-4 h-4 rotate-180" />
                                            Show less
                                          </>
                                        ) : (
                                          <>
                                            <ChevronDown className="w-4 h-4" />
                                            Show all {activeItems.length} items
                                          </>
                                        )}
                                      </span>
                                    </button>
                                  )}
                                </>
                              );
                            })()}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Categories */}
      <div className="space-y-4">
        <h2 className="text-xl font-bold text-glass font-heading flex items-center gap-2">
          <TrendingDown className="w-5 h-5" />
          Tasks to Complete
        </h2>

        {categories.filter(cat => cat.items.length > 0).length === 0 ? (
          <div className="glass-card p-8 text-center">
            <Sparkles className="w-12 h-12 text-green-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-glass mb-2 font-heading">All Caught Up!</h3>
            <p className="text-glass-secondary font-body">
              Your collection is in great shape. Nothing needs attention right now!
            </p>
          </div>
        ) : (
          categories
            .filter(cat => cat.items.length > 0)
            .sort((a, b) => b.items.length - a.items.length)
            .map(category => {
              const colors = getCategoryColor(category.color);
              const Icon = category.icon;
              const isExpanded = expandedCategory === category.id;
              const percentage = totalItems > 0 
                ? Math.round(((totalItems - category.items.length) / totalItems) * 100) 
                : 100;

              return (
                <div key={category.id} className="glass-card overflow-hidden">
                  {/* Category Header */}
                  <button
                    onClick={() => setExpandedCategory(isExpanded ? null : category.id)}
                    className="w-full p-4 flex items-center justify-between hover:bg-slate-800/40 transition-default"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`p-3 rounded-lg ${colors.bg}`}>
                        <Icon className={`w-6 h-6 ${colors.text}`} />
                      </div>
                      <div className="text-left">
                        <div className="font-semibold text-glass font-body flex items-center gap-2">
                          {category.emoji} {category.title}
                          <span className={`px-2 py-0.5 text-xs rounded-full ${colors.bg} ${colors.text}`}>
                            {category.items.length}
                          </span>
                        </div>
                        <div className="text-sm text-glass-secondary font-body">{category.description}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="hidden sm:block w-32">
                        <div className="text-xs text-glass-muted text-right mb-1">{percentage}% done</div>
                        <div className="w-full h-2 bg-slate-700 rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${getProgressColor(percentage)}`}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                      <ChevronRight className={`w-5 h-5 text-glass-secondary transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                    </div>
                  </button>

                  {/* Expanded Items */}
                  {isExpanded && (
                    <div className="border-t border-slate-700">
                      <div className="p-3 bg-slate-800/40 flex justify-between items-center">
                        <span className="text-sm text-glass-secondary font-body">
                          Showing {Math.min(10, category.items.length)} of {category.items.length} items
                        </span>
                        <Link
                          to={`/collection?filter=${category.linkFilter}`}
                          className="text-sm text-purple-400 hover:text-purple-300 flex items-center gap-1 font-body"
                        >
                          <Filter className="w-4 h-4" />
                          View All in Collection
                        </Link>
                      </div>
                      <div className="divide-y divide-slate-700">
                        {category.items.slice(0, 10).map(item => (
                          <Link
                            key={item.id}
                            to={`/collection?item=${item.id}`}
                            className="flex items-center gap-4 p-3 hover:bg-slate-800/40 transition-default"
                          >
                            {/* Thumbnail */}
                            <div className="w-12 h-12 rounded-lg overflow-hidden bg-slate-700 flex-shrink-0">
                              {(item.photos?.[0] || item.photoUrl || item.swatchPhotos?.[0]) ? (
                                <img
                                  src={typeof item.photos?.[0] === 'string' ? item.photos[0] : item.photos?.[0]?.url || item.photoUrl || item.swatchPhotos?.[0]}
                                  alt={item.name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-slate-500">
                                  <Camera className="w-5 h-5" />
                                </div>
                              )}
                            </div>
                            {/* Info */}
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-glass truncate font-body">{item.name}</div>
                              <div className="text-sm text-glass-secondary truncate font-body">{item.brand}</div>
                            </div>
                            {/* Action hint */}
                            <Eye className="w-4 h-4 text-glass-muted" />
                          </Link>
                        ))}
                      </div>
                      {category.items.length > 10 && (
                        <div className="p-3 bg-slate-800/40 text-center">
                          <Link
                            to={`/collection?filter=${category.linkFilter}`}
                            className="text-sm text-purple-400 hover:text-purple-300 font-body"
                          >
                            +{category.items.length - 10} more items →
                          </Link>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
        )}
      </div>

      {/* Tips */}
      <div className="mt-8 p-4 bg-slate-800/40 rounded-lg border border-slate-700">
        <h3 className="font-semibold text-glass mb-2 font-body">💡 Tips for completing your collection</h3>
        <ul className="text-sm text-glass-secondary space-y-1 font-body">
          <li>• Click any item to open it directly in your collection</li>
          <li>• Use the "View All in Collection" link to filter and bulk-edit</li>
          <li>• Mark items as "Swatched" to keep track of your progress</li>
          <li>• Add color tags to make your collection searchable</li>
        </ul>
      </div>

      {/* Data Management Section */}
      {/* Tagging Tools */}
      <div className="glass-card p-6 mt-8">
        <h2 className="text-xl font-bold text-glass mb-4 flex items-center gap-2 font-body">
          🏷️ Tagging Tools
        </h2>
        <p className="text-glass-secondary text-sm mb-4 font-body">
          Tag your polishes with colors and your plates with descriptive keywords.
        </p>
        <div className="space-y-4">
          {/* Color Tagger */}
          <div className="p-4 bg-slate-800/40 rounded-lg border border-slate-600">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <h3 className="font-semibold text-glass mb-1 font-body">Color Tagger</h3>
                <p className="text-sm text-glass-secondary font-body">
                  Tag polish colors by clicking on photos. Works through polishes that are missing color data.
                </p>
              </div>
              <button
                onClick={() => setShowColorTagger(true)}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-lg hover:from-pink-600 hover:to-purple-600 transition-default font-medium whitespace-nowrap"
              >
                🎨 Color Tagger
              </button>
            </div>
          </div>

          {/* Plate Tagger */}
          <div className="p-4 bg-slate-800/40 rounded-lg border border-slate-600">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <h3 className="font-semibold text-glass mb-1 font-body">Plate Tagger</h3>
                <p className="text-sm text-glass-secondary font-body">
                  Tag plate images with descriptive keywords to make them easier to search and find.
                </p>
              </div>
              <button
                onClick={() => setShowPlateTagger(true)}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-lg hover:from-purple-600 hover:to-indigo-600 transition-default font-medium whitespace-nowrap"
              >
                🔲 Plate Tagger
              </button>
            </div>
          </div>

          {/* Brand Category Manager */}
          <div className="p-4 bg-slate-800/40 rounded-lg border border-slate-600">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <h3 className="font-semibold text-glass mb-1 font-body">Brand Categories</h3>
                <p className="text-sm text-glass-secondary font-body">
                  Classify your polish brands as indie, boutique, or mainstream to see brand type breakdowns on profiles.
                </p>
              </div>
              <button
                onClick={() => setShowBrandManager(true)}
                className="flex items-center gap-2 px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-default font-medium"
              >
                <Settings className="w-4 h-4" />
                Manage
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="glass-card p-6 mt-8">
        <h2 className="text-xl font-bold text-glass mb-4 flex items-center gap-2 font-body">
          📦 Data Management
        </h2>
        
        <p className="text-glass-secondary text-sm mb-4 font-body">
          Clean up, export, and import your collection data.
        </p>

        <div className="space-y-4">
          {/* Clean Up Collection */}
          <div className="p-4 bg-slate-800/40 rounded-lg border border-slate-600">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <h3 className="font-semibold text-glass mb-1 font-body">Clean Up Collection</h3>
                <p className="text-sm text-glass-secondary font-body">
                  Find and fix data quality issues like placeholder values, extra whitespace, potential duplicates, and inconsistent brand names.
                </p>
              </div>
              <button
                onClick={() => setShowCleanupModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-default font-medium"
              >
                <Sparkles className="w-4 h-4" />
                Clean Up
              </button>
            </div>
          </div>

          {/* Export Collection */}
          <div className="p-4 bg-slate-800/40 rounded-lg border border-slate-600">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <h3 className="font-semibold text-glass mb-1 font-body">Export Collection (CSV)</h3>
                <p className="text-sm text-glass-secondary font-body">
                  Download your entire collection as a CSV file. Includes all polish details, release info, tags, and notes.
                </p>
              </div>
              <button
                onClick={handleExportCollection}
                disabled={isExporting}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-default font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isExporting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    Export
                  </>
                )}
              </button>
            </div>
            
            {/* Progress/Status */}
            {exportProgress && (
              <div className={`mt-3 text-sm flex items-center gap-2 ${exportSuccess ? 'text-green-400' : 'text-glass-secondary'}`}>
                {exportSuccess && <Check className="w-4 h-4" />}
                {exportProgress}
              </div>
            )}
            
            {/* Error */}
            {exportError && (
              <div className="mt-3 text-sm text-red-400 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {exportError}
              </div>
            )}
          </div>

          {/* Import link */}
          <div className="p-4 bg-slate-800/40 rounded-lg border border-slate-600">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <h3 className="font-semibold text-glass mb-1 font-body">Import Collection</h3>
                <p className="text-sm text-glass-secondary font-body">
                  Import polishes from a CSV file or other sources.
                </p>
              </div>
              <Link
                to="/import"
                className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-default font-medium"
              >
                Go to Import
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Collection Cleanup Modal */}
      {showCleanupModal && (
        <CollectionCleanup onClose={() => setShowCleanupModal(false)} />
      )}

      {/* Color Tagger Modal */}
      {showColorTagger && (
        <ColorTaggerModal
          items={allItems.filter(item =>
            item.itemType === 'polish' &&
            ((item.photos?.length ?? 0) > 0 || item.swatchPhotos?.length > 0) &&
            !item.primaryColor && !item.colorFamily?.length
          )}
          allItems={allItems.filter(item =>
            item.itemType === 'polish' &&
            ((item.photos?.length ?? 0) > 0 || item.swatchPhotos?.length > 0)
          )}
          userId={userId}
          onClose={async () => {
            setShowColorTagger(false);
            await loadItems();
          }}
          onUpdate={async () => {
            await loadItems();
          }}
        />
      )}

      {/* Brand Category Manager Modal */}
      {showBrandManager && (
        <BrandCategoryManager
          isOpen={showBrandManager}
          onClose={() => setShowBrandManager(false)}
          brands={uniqueBrands}
          onCategoriesUpdated={() => window.location.reload()}
        />
      )}

      {/* Plate Tagger Modal */}
      {showPlateTagger && (
        <PlateTaggerModal
          items={allItems.filter(item =>
            item.itemType === 'plate' &&
            (item.photos?.length ?? 0) > 0 &&
            (!item.imageTags || item.imageTags.length === 0)
          )}
          allItems={allItems.filter(item =>
            item.itemType === 'plate' &&
            (item.photos?.length ?? 0) > 0
          )}
          userId={userId}
          onClose={async () => {
            setShowPlateTagger(false);
            await loadItems();
          }}
          onUpdate={async () => {
            await loadItems();
          }}
        />
      )}
    </div>
  );
}
